import React, { useEffect, useState } from 'react';
import { Container, Form, Button, Image, InputGroup } from 'react-bootstrap';
import { EyeFill, EyeSlashFill } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";

function Login() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState(''); // Email or Phone
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPhoneLogin, setIsPhoneLogin] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [codeSent, setCodeSent] = useState(false);

  useEffect(() => {
    const auth = localStorage.getItem('projectUserAuth');
    if (auth) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    // Initialize reCAPTCHA for phone authentication
    if (isPhoneLogin && !window.recaptchaVerifier) {
      const auth = getAuth();
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          'size': 'invisible',
          'callback': () => {
            // reCAPTCHA solved
          }
        });
      } catch (error) {
        console.error("reCAPTCHA initialization error:", error);
      }
    }
  }, [isPhoneLogin]);

  // Detect if input is email or phone
  const detectInputType = (value) => {
    // Check if it's a phone number (only digits, 10 digits for India)
    const phoneRegex = /^\d{10}$/; // Exactly 10 digits
    const partialPhoneRegex = /^\d+$/; // Only digits (partial input)
    
    if (phoneRegex.test(value) || (partialPhoneRegex.test(value) && value.length <= 10)) {
      setIsPhoneLogin(true);
    } else {
      setIsPhoneLogin(false);
    }
  };

  const handleIdentifierChange = (e) => {
    let value = e.target.value;
    
    // If input contains only digits, restrict to 10 digits
    if (/^\d*$/.test(value)) {
      value = value.slice(0, 10); // Limit to 10 digits
    }
    
    setIdentifier(value);
    detectInputType(value);
  };

  const handleEmailLogin = async () => {
    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, identifier, password);
      
      window.localStorage.setItem('projectUserAuth', 'true');
      window.localStorage.setItem('projectUserUID', userCredential.user.uid);
      window.localStorage.setItem('projectUserDisplayName', userCredential.user.displayName);

      const customAttributes = userCredential.user.reloadUserInfo.customAttributes;
      if (customAttributes) {
        const parsedAttributes = JSON.parse(customAttributes);
        if (parsedAttributes.role) {
          window.localStorage.setItem('projectUserRole', parsedAttributes.role);
        }
      }

      const token = await userCredential.user.getIdToken();
      window.localStorage.setItem('projectAccessToken', token);

      navigate('/', { replace: true });
    } catch (error) {
      if (error.code === "auth/user-not-found") {
        setMessage("User not found. Please check your email.");
      } else if (error.code === "auth/wrong-password") {
        setMessage("Incorrect password. Please try again.");
      } else if (error.code === "auth/invalid-email") {
        setMessage("Invalid email format.");
      } else if (error.code === "auth/invalid-credential") {
        setMessage("Invalid credentials. Please try again.");
      } else {
        setMessage("Login failed. Please check your credentials.");
      }
    }
  };

  const handlePhoneLogin = async () => {
    // Validate phone number (should be 10 digits)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(identifier)) {
      setMessage("Please enter a valid 10-digit phone number");
      return;
    }

    // Add India country code (+91)
    const phoneWithCountryCode = `+91${identifier}`;

    try {
      const auth = getAuth();
      const appVerifier = window.recaptchaVerifier;
      
      const confirmation = await signInWithPhoneNumber(auth, phoneWithCountryCode, appVerifier);
      setConfirmationResult(confirmation);
      setCodeSent(true);
      setMessage("Verification code sent to your phone!");
    } catch (error) {
      console.error("Error sending verification code:", error);
      if (error.code === "auth/invalid-phone-number") {
        setMessage("Invalid phone number. Please enter a valid 10-digit number.");
      } else if (error.code === "auth/too-many-requests") {
        setMessage("Too many requests. Please try again later.");
      } else {
        setMessage("Failed to send verification code. Please try again.");
      }
      
      // Reset reCAPTCHA
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    }
  };

  const handleVerifyCode = async () => {
    if (!confirmationResult) {
      setMessage("Please request a verification code first.");
      return;
    }

    try {
      const userCredential = await confirmationResult.confirm(verificationCode);
      
      window.localStorage.setItem('projectUserAuth', 'true');
      window.localStorage.setItem('projectUserUID', userCredential.user.uid);
      window.localStorage.setItem('projectUserDisplayName', userCredential.user.displayName || '');

      const customAttributes = userCredential.user.reloadUserInfo?.customAttributes;
      if (customAttributes) {
        const parsedAttributes = JSON.parse(customAttributes);
        if (parsedAttributes.role) {
          window.localStorage.setItem('projectUserRole', parsedAttributes.role);
        }
      }

      const token = await userCredential.user.getIdToken();
      window.localStorage.setItem('projectAccessToken', token);

      navigate('/', { replace: true });
    } catch (error) {
      console.error("Error verifying code:", error);
      if (error.code === "auth/invalid-verification-code") {
        setMessage("Invalid verification code. Please try again.");
      } else if (error.code === "auth/code-expired") {
        setMessage("Verification code expired. Please request a new one.");
        setCodeSent(false);
        setConfirmationResult(null);
      } else {
        setMessage("Verification failed. Please try again.");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (codeSent) {
      // Verify the code for phone login
      await handleVerifyCode();
    } else if (isPhoneLogin) {
      // Send verification code for phone
      await handlePhoneLogin();
    } else {
      // Email login with password
      await handleEmailLogin();
    }
  };

  const resetPhoneLogin = () => {
    setCodeSent(false);
    setConfirmationResult(null);
    setVerificationCode('');
    setMessage('');
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
  };

  return (
    <Container fluid style={{ backgroundColor: "#FAFAFA", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className='col-12 col-md-3 p-5' style={{ backgroundColor: "rgba(250, 250, 250, 0.95)", borderRadius: '10px', borderColor: '#e0e0e0', borderStyle: 'solid', borderWidth: '1px', boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)', textAlign: 'center' }}>
        <Image src={require('../assets/site-logo.png')} style={{ width: '50px', height: 'auto' }} />
        <p style={{ color: "#2F2F2F" }}>Log in to your account</p>

        <Form onSubmit={handleSubmit}>
          {!codeSent ? (
            <>
              {/* Single Input for Email or Phone */}
              <Form.Group className="mb-4 mt-5" controlId="formIdentifier">
                <Form.Control
                  type="text"
                  placeholder="Email or Phone (10 digits)"
                  className="custom-form-control"
                  onChange={handleIdentifierChange}
                  value={identifier}
                  required
                />
                <Form.Text className="text-muted" style={{ fontSize: '0.85rem' }}>
                  {isPhoneLogin ? "📱 Phone login detected (India +91)" : "📧 Email login detected"}
                </Form.Text>
              </Form.Group>

              {/* Password field - only show for email login */}
              {!isPhoneLogin && (
                <Form.Group className="mb-4" controlId="formBasicPassword">
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      style={{ backgroundColor: '#FFFFFF', color: '#2F2F2F', borderColor: '#e0e0e0', borderStyle: 'solid', borderWidth: '1px' }}
                      className="custom-form-control"
                      onChange={(e) => setPassword(e.target.value)}
                      value={password}
                      required
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeSlashFill /> : <EyeFill />}
                    </Button>
                  </InputGroup>
                </Form.Group>
              )}

              <Button style={{ backgroundColor: "#e75b12", borderColor: '#e75b12' }} className='w-100' type="submit">
                {isPhoneLogin ? "Send Verification Code" : "Sign In"}
              </Button>
            </>
          ) : (
            <>
              {/* Verification Code Input for Phone Login */}
              <Form.Group className="mb-4 mt-5" controlId="formVerificationCode">
                <Form.Control
                  type="text"
                  placeholder="Enter 6-digit code"
                  className="custom-form-control"
                  onChange={(e) => setVerificationCode(e.target.value)}
                  value={verificationCode}
                  maxLength={6}
                  required
                />
                <Form.Text className="text-muted">
                  Code sent to {identifier}
                </Form.Text>
              </Form.Group>

              <Button style={{ backgroundColor: "#e75b12", borderColor: '#e75b12' }} className='w-100 mb-2' type="submit">
                Verify & Sign In
              </Button>
              
              <Button 
                variant="outline-secondary" 
                className='w-100' 
                onClick={resetPhoneLogin}
                type="button"
              >
                Back
              </Button>
            </>
          )}
        </Form>

        {/* reCAPTCHA container (invisible) */}
        <div id="recaptcha-container"></div>

        {message && (
          <p style={{ 
            color: message.includes('sent') ? 'green' : 'red', 
            marginTop: '10px',
            fontSize: '0.9rem'
          }}>
            {message}
          </p>
        )}
      </div>
    </Container>
  );
}

export default Login;